import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getCourse, getLesson } from '../services/courseService';
import { useAuth } from '../contexts/AuthContext';
import VideoPlayer from './VideoPlayer';
import '../styles/LessonView.css';

const LessonView = () => {
  const { appId, courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [course, setCourse] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [videoError, setVideoError] = useState(null);
  
  // Extract the application ID if not provided directly
  const applicationId = appId || 1; // Default to app ID 1 (the-faithful-storyteller)
  
  useEffect(() => {
    fetchCourseAndLesson();
  }, [courseId, lessonId]);
  
  const fetchCourseAndLesson = async () => {
    try {
      console.log(`🔍 LessonView: Fetching course ${courseId} and lesson ${lessonId} for app ${applicationId}`);
      setLoading(true);
      
      // Get the auth token
      const token = localStorage.getItem('token_admin');
      // Get the current domain
      const domain = window.location.hostname;
      
      // Normalize all IDs to ensure they're numbers
      const numericAppId = parseInt(applicationId, 10) || 1;
      const numericCourseId = parseInt(courseId, 10);
      const numericLessonId = parseInt(lessonId, 10);
      
      console.log(`🔍 LessonView: Using numeric app ID: ${numericAppId}, course ID: ${numericCourseId}, lesson ID: ${numericLessonId}`);
      console.log("🔍 LessonView: Auth token present:", !!token);
      
      // Fetch course details using app-specific endpoint that works
      console.log(`🔍 LessonView: Fetching course details from /api/apps/${numericAppId}/courses/${numericCourseId}`);
      const courseResponse = await fetch(`/api/apps/${numericAppId}/courses/${numericCourseId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          'X-App-Domain': domain,
          'X-SaltMarsh-App-Context': 'admin'
        },
        credentials: 'include'
      });
      
      if (courseResponse.status === 200) {
        const courseData = await courseResponse.json();
        console.log("🔍 LessonView: Course data received:", courseData);
        
        // Handle different possible response structures
        const courseObject = courseData.data || courseData.course || courseData;
        setCourse(courseObject);
        
        // Fetch lesson details using the correct endpoint from the API documentation
        console.log(`🔍 LessonView: Fetching lesson from /api/lessons/${numericLessonId}`);
        const lessonResponse = await fetch(`/api/lessons/${numericLessonId}`, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
            'X-App-Domain': domain,
            'X-SaltMarsh-App-Context': 'admin'
          },
          credentials: 'include'
        });
        
        if (lessonResponse.status === 200) {
          const lessonData = await lessonResponse.json();
          console.log("🔍 LessonView: Lesson data received:", lessonData);
          
          // Handle different possible response structures
          const lessonObject = lessonData.data || lessonData.lesson || lessonData;
          setLesson(lessonObject);
          setError(null);
        } else {
          console.error(`🔍 LessonView: Failed to fetch lesson, status: ${lessonResponse.status}`);
          setError("Failed to load lesson. Please try again later.");
          setLesson(null);
        }
      } else {
        console.error(`🔍 LessonView: Failed to fetch course, status: ${courseResponse.status}`);
        setError("Failed to load course. Please try again later.");
        setCourse(null);
        setLesson(null);
      }
    } catch (err) {
      console.error('🔍 LessonView: Error fetching course and lesson:', err);
      setError(`Failed to load content: ${err.message || 'Unknown error'}`);
      setCourse(null);
      setLesson(null);
    } finally {
      setLoading(false);
    }
  };
  
  // Check if user has app_editor role
  const isAppEditor = currentUser?.roles?.some(role => 
    role.name === 'app_editor' || role.name === 'admin' || role.name === 'superadmin'
  );
  
  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">{error}</div>
        <Link to={`/courses/${courseId}`} className="btn btn-primary">
          Back to Course
        </Link>
      </div>
    );
  }
  
  if (!lesson) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning">Lesson not found.</div>
        <Link to={`/courses/${courseId}`} className="btn btn-primary">
          Back to Course
        </Link>
      </div>
    );
  }
  
  return (
    <div className="lesson-view container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>{lesson.title}</h1>
          {course && (
            <h5 className="text-muted">Course: {course.title}</h5>
          )}
        </div>
        <div className="d-flex gap-2">
          {isAppEditor && (
            <Link 
              to={`/apps/${applicationId}/courses/${courseId}/lessons/${lessonId}/edit`} 
              className="btn btn-primary"
            >
              <i className="fas fa-edit"></i> Edit Lesson
            </Link>
          )}
          <Link 
            to={`/courses/${courseId}`} 
            className="btn btn-secondary"
          >
            <i className="fas fa-arrow-left"></i> Back to Course
          </Link>
        </div>
      </div>
      
      {/* Video content */}
      {lesson.video_url && (
        <div className="lesson-video mb-4">
          <VideoPlayer 
            url={lesson.video_url} 
            thumbnail={lesson.thumbnail_url || course?.thumbnail_url}
            onError={(error) => setVideoError(error)}
          />
          {videoError && (
            <div className="alert alert-warning mt-2">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {videoError}
            </div>
          )}
        </div>
      )}
      
      {/* Lesson content */}
      <div className="lesson-content card">
        <div className="card-body">
          {lesson.description && (
            <div className="mb-4">
              <h5>Description</h5>
              <p>{lesson.description}</p>
            </div>
          )}
          
          {lesson.content && (
            <div className="lesson-text-content">
              <h5>Content</h5>
              <div dangerouslySetInnerHTML={{ __html: lesson.content }} />
            </div>
          )}
          
          {lesson.resources && lesson.resources.length > 0 && (
            <div className="mt-4">
              <h5>Resources</h5>
              <ul className="list-group">
                {lesson.resources.map((resource, index) => (
                  <li key={index} className="list-group-item">
                    <a href={resource.url} target="_blank" rel="noopener noreferrer">
                      <i className="fas fa-external-link-alt me-2"></i>
                      {resource.title || 'Resource'}
                    </a>
                    {resource.description && (
                      <p className="mb-0 small text-muted mt-1">{resource.description}</p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LessonView; 